import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import {
  PageContainer, PageHeader,
  Card, CardHeader, StatCard as UIStatCard,
  Table, Thead, Th, Td, TRow,
  TabGroup, Muted, EmptyState,
  Button, Badge, CategoryBadge,
} from "../ui";

type ViewType = "date-seasonal" | "products" | "categories" | "tender" | "customers";

const VIEWS: Array<{ key: string; label: string }> = [
  { key: "date-seasonal", label: "Date & Seasonal" },
  { key: "products", label: "Products" },
  { key: "categories", label: "Categories" },
  { key: "tender", label: "Payment Type" },
  { key: "customers", label: "Customers" },
];

const KNOWN_HOLIDAYS: Record<string, string> = {
  "2025-12-24": "Christmas Eve",
  "2025-12-25": "Christmas Day",
  "2025-12-31": "New Year's Eve",
  "2026-01-01": "New Year's Day",
  "2026-02-14": "Valentine's Day",
  "2026-03-17": "St. Patrick's Day",
  "2026-04-03": "Good Friday",
  "2026-04-05": "Easter",
  "2026-05-25": "Memorial Day",
};

function fmtDollars(v: unknown): string {
  const n = Number(v) || 0;
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(v: unknown): string {
  return Number(v || 0).toFixed(1) + "%";
}

function fmtDate(v: unknown): string {
  if (!v) return "-";
  const d = new Date(String(v));
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function fmtNum(v: unknown): string {
  const n = Number(v) || 0;
  return n.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function marginColor(pct: number): string {
  if (pct < 15) return "var(--rbw-danger)";
  if (pct < 25) return "#ca8a04";
  return "var(--rbw-success)";
}

export default function DetailedSales() {
  const [view, setView] = useState<ViewType>("date-seasonal");
  // Everything Karen holds, through today. The old fixed end date (2026-06-04)
  // hid four months of imported sales and read as a gap in the history (10/6).
  const [startDate, setStartDate] = useState("2025-12-18");
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [productSort, setProductSort] = useState<{ col: string; asc: boolean }>({ col: "revenue", asc: false });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      switch (view) {
        case "date-seasonal":
          response = await api.get(`/reports/detailed/date-seasonal?start=${startDate}&end=${endDate}`);
          break;
        case "products":
          response = await api.get(`/reports/detailed/products?start=${startDate}&end=${endDate}`);
          break;
        case "categories":
          response = await api.get(`/reports/detailed/categories?start=${startDate}&end=${endDate}`);
          break;
        case "tender":
          response = await api.get(`/reports/detailed/tender?start=${startDate}&end=${endDate}`);
          break;
        case "customers":
          response = await api.get(`/reports/detailed/customers?start=${startDate}&end=${endDate}`);
          break;
      }
      setData(response?.data || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [view, startDate, endDate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExport = async () => {
    const groupMap: Record<string, string> = {
      "date-seasonal": "daily",
      "products": "product",
      "categories": "category",
      "tender": "tender",
      "customers": "customer",
    };
    try {
      const response = await api.get(
        `/reports/detailed/export?start=${startDate}&end=${endDate}&group_by=${groupMap[view]}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `detailed-sales-${groupMap[view]}-${startDate}-to-${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  return (
    <PageContainer wide>
      <PageHeader
        title="Detailed Sales Report"
        subtitle="mPower historical data: Dec 2025 - Jun 2026"
        action={
          <Button onClick={handleExport} variant="secondary" size="sm">
            Export CSV
          </Button>
        }
      />

      <TabGroup
        tabs={VIEWS}
        active={view}
        onChange={(key) => setView(key as ViewType)}
      />

      {/* Date range controls */}
      <div className="flex gap-3 mb-6 items-center">
        <label className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>From</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="px-3 py-2 rounded-xl outline-none text-sm"
          style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
        />
        <label className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>To</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="px-3 py-2 rounded-xl outline-none text-sm"
          style={{ border: "2px solid var(--rbw-border)", background: "var(--rbw-bg-card)", color: "var(--rbw-navy)" }}
        />
        <div className="flex gap-1">
          {[
            { label: "All", s: "2025-12-18", e: "2026-06-04" },
            { label: "Jan", s: "2026-01-01", e: "2026-02-01" },
            { label: "Feb", s: "2026-02-01", e: "2026-03-01" },
            { label: "Mar", s: "2026-03-01", e: "2026-04-01" },
            { label: "Apr", s: "2026-04-01", e: "2026-05-01" },
            { label: "May", s: "2026-05-01", e: "2026-06-01" },
          ].map((p) => (
            <button
              key={p.label}
              onClick={() => { setStartDate(p.s); setEndDate(p.e); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={startDate === p.s && endDate === p.e
                ? { background: "var(--rbw-navy)", color: "#fff" }
                : { background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }
              }
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><Muted>Loading...</Muted></div>
      ) : !data ? (
        <Card><EmptyState message="No data" /></Card>
      ) : (
        <>
          {view === "date-seasonal" && <DateSeasonalView data={data} />}
          {view === "products" && <ProductView data={data} sort={productSort} setSort={setProductSort} />}
          {view === "categories" && <CategoryView data={data} />}
          {view === "tender" && <TenderView data={data} />}
          {view === "customers" && <CustomerView data={data} />}
        </>
      )}
    </PageContainer>
  );
}

// ============================================================
// VIEW 1: DATE & SEASONAL
// ============================================================

function DateSeasonalView({ data }: { data: Record<string, unknown> }) {
  const summary = data.summary as Record<string, unknown>;
  const daily = (data.daily || []) as Array<Record<string, unknown>>;
  const byDow = (data.byDayOfWeek || []) as Array<Record<string, unknown>>;
  const byMonth = (data.byMonth || []) as Array<Record<string, unknown>>;
  const peaks = (data.peakDays || []) as Array<Record<string, unknown>>;

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <UIStatCard label="Total Revenue" value={fmtDollars(summary.total_revenue)} />
        <UIStatCard label="Selling Days" value={String(summary.selling_days || 0)} />
        <UIStatCard label="Avg Daily Revenue" value={fmtDollars(summary.avg_daily_revenue)} variant="gold" />
        <UIStatCard label="Total Margin" value={fmtDollars(summary.total_margin)} variant="green" />
        <UIStatCard label="Margin %" value={fmtPct(summary.margin_pct)} variant={Number(summary.margin_pct) >= 25 ? "green" : "gold"} />
      </div>

      {/* Monthly breakdown */}
      <Card className="mb-6">
        <CardHeader title="Monthly Breakdown" />
        <Table>
          <Thead>
            <Th>Month</Th>
            <Th align="right">Days</Th>
            <Th align="right">Revenue</Th>
            <Th align="right">Cost</Th>
            <Th align="right">Margin</Th>
            <Th align="right">Margin %</Th>
            <Th align="right">Avg/Day</Th>
            <Th align="right">Transactions</Th>
          </Thead>
          <tbody>
            {byMonth.map((r, i) => (
              <TRow key={i} index={i}>
                <Td bold>{String(r.month_label)}</Td>
                <Td align="right">{String(r.selling_days)}</Td>
                <Td align="right" bold>{fmtDollars(r.revenue)}</Td>
                <Td align="right">{fmtDollars(r.cost)}</Td>
                <Td align="right">{fmtDollars(r.margin)}</Td>
                <Td align="right">
                  <span style={{ color: marginColor(Number(r.margin_pct)), fontWeight: 600 }}>
                    {fmtPct(r.margin_pct)}
                  </span>
                </Td>
                <Td align="right">{fmtDollars(r.avg_daily_revenue)}</Td>
                <Td align="right">{fmtNum(r.transactions)}</Td>
              </TRow>
            ))}
          </tbody>
        </Table>
      </Card>

      {/* Day of week breakdown */}
      <Card className="mb-6">
        <CardHeader title="Day of Week Analysis" />
        <Table>
          <Thead>
            <Th>Day</Th>
            <Th align="right">Selling Days</Th>
            <Th align="right">Total Revenue</Th>
            <Th align="right">Avg Revenue</Th>
            <Th align="right">Avg Transactions</Th>
          </Thead>
          <tbody>
            {byDow.map((r, i) => {
              const avgRev = Number(r.avg_daily_revenue) || 0;
              const maxAvg = Math.max(...byDow.map((d) => Number(d.avg_daily_revenue) || 0));
              const barWidth = maxAvg > 0 ? (avgRev / maxAvg * 100) : 0;
              return (
                <TRow key={i} index={i}>
                  <Td bold>{String(r.day_name)}</Td>
                  <Td align="right">{String(r.selling_days)}</Td>
                  <Td align="right">{fmtDollars(r.total_revenue)}</Td>
                  <Td align="right">
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-24 h-3 rounded-full overflow-hidden" style={{ background: "var(--rbw-gray-light)" }}>
                        <div className="h-full rounded-full" style={{ width: barWidth + "%", background: "var(--rbw-gold)" }} />
                      </div>
                      <span className="font-semibold" style={{ minWidth: 70, textAlign: "right" }}>{fmtDollars(r.avg_daily_revenue)}</span>
                    </div>
                  </Td>
                  <Td align="right">{fmtNum(r.avg_transactions)}</Td>
                </TRow>
              );
            })}
          </tbody>
        </Table>
      </Card>

      {/* Peak days */}
      {peaks.length > 0 && (
        <Card className="mb-6">
          <CardHeader title={`Peak Days (>150% of avg ${fmtDollars(summary.avg_daily_revenue)})`} />
          <Table>
            <Thead>
              <Th>Date</Th>
              <Th>Day</Th>
              <Th align="right">Revenue</Th>
              <Th align="right">% of Avg</Th>
              <Th align="right">Transactions</Th>
              <Th>Holiday</Th>
            </Thead>
            <tbody>
              {peaks.map((r, i) => {
                const dateStr = String(r.sale_date).substring(0, 10);
                const holiday = KNOWN_HOLIDAYS[dateStr] || "";
                return (
                  <TRow key={i} index={i}>
                    <Td>{fmtDate(r.sale_date)}</Td>
                    <Td>{String(r.day_name)}</Td>
                    <Td align="right" bold>{fmtDollars(r.revenue)}</Td>
                    <Td align="right">
                      <span style={{ color: "var(--rbw-success)", fontWeight: 600 }}>{String(r.pct_of_avg)}%</span>
                    </Td>
                    <Td align="right">{String(r.transactions)}</Td>
                    <Td>{holiday && <Badge variant="gold">{holiday}</Badge>}</Td>
                  </TRow>
                );
              })}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Daily detail */}
      <Card>
        <CardHeader title="Daily Detail" />
        <div style={{ maxHeight: 500, overflowY: "auto" }}>
          <Table>
            <Thead>
              <Th>Date</Th>
              <Th>Day</Th>
              <Th align="right">Revenue</Th>
              <Th align="right">Cost</Th>
              <Th align="right">Margin %</Th>
              <Th align="right">Transactions</Th>
              <Th>Holiday</Th>
            </Thead>
            <tbody>
              {daily.map((r, i) => {
                const dateStr = String(r.sale_date).substring(0, 10);
                const holiday = KNOWN_HOLIDAYS[dateStr] || "";
                return (
                  <TRow key={i} index={i}>
                    <Td>{fmtDate(r.sale_date)}</Td>
                    <Td>{String(r.day_name)}</Td>
                    <Td align="right" bold>{fmtDollars(r.revenue)}</Td>
                    <Td align="right">{fmtDollars(r.cost)}</Td>
                    <Td align="right">
                      <span style={{ color: marginColor(Number(r.margin_pct)), fontWeight: 600 }}>
                        {fmtPct(r.margin_pct)}
                      </span>
                    </Td>
                    <Td align="right">{String(r.transactions)}</Td>
                    <Td>{holiday && <Badge variant="gold">{holiday}</Badge>}</Td>
                  </TRow>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// VIEW 2: PRODUCTS
// ============================================================

function ProductView({ data, sort, setSort }: {
  data: Record<string, unknown>;
  sort: { col: string; asc: boolean };
  setSort: (s: { col: string; asc: boolean }) => void;
}) {
  const rows = ((data.data || []) as Array<Record<string, unknown>>);

  const sorted = [...rows].sort((a, b) => {
    const av = Number(a[sort.col]) || 0;
    const bv = Number(b[sort.col]) || 0;
    return sort.asc ? av - bv : bv - av;
  });

  const toggleSort = (col: string) => {
    setSort({ col, asc: sort.col === col ? !sort.asc : false });
  };

  const sortIndicator = (col: string) => {
    if (sort.col !== col) return "";
    return sort.asc ? " ^" : " v";
  };

  return (
    <Card>
      <CardHeader title={`Product Analysis (${rows.length} products)`} />
      <div style={{ maxHeight: 700, overflowY: "auto" }}>
        <Table>
          <Thead>
            <Th>Product</Th>
            <Th>Category</Th>
            <Th>Size</Th>
            <Th align="right">
              <button onClick={() => toggleSort("units_sold")} className="font-semibold uppercase text-xs tracking-wider" style={{ color: "var(--rbw-gray)" }}>
                Units{sortIndicator("units_sold")}
              </button>
            </Th>
            <Th align="right">
              <button onClick={() => toggleSort("revenue")} className="font-semibold uppercase text-xs tracking-wider" style={{ color: "var(--rbw-gray)" }}>
                Revenue{sortIndicator("revenue")}
              </button>
            </Th>
            <Th align="right">
              <button onClick={() => toggleSort("cost")} className="font-semibold uppercase text-xs tracking-wider" style={{ color: "var(--rbw-gray)" }}>
                Cost{sortIndicator("cost")}
              </button>
            </Th>
            <Th align="right">
              <button onClick={() => toggleSort("margin_pct")} className="font-semibold uppercase text-xs tracking-wider" style={{ color: "var(--rbw-gray)" }}>
                Margin %{sortIndicator("margin_pct")}
              </button>
            </Th>
            <Th align="right">
              <button onClick={() => toggleSort("days_sold")} className="font-semibold uppercase text-xs tracking-wider" style={{ color: "var(--rbw-gray)" }}>
                Days Sold{sortIndicator("days_sold")}
              </button>
            </Th>
          </Thead>
          <tbody>
            {sorted.map((r, i) => {
              const mPct = Number(r.margin_pct) || 0;
              const costUnverified = r.cost_unverified;
              return (
                <TRow key={i} index={i}>
                  <Td>
                    <div className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>
                      {String(r.product_name)}
                    </div>
                    <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                      SKU: {String(r.sku_number)}
                      {r.brand ? " | " + String(r.brand) : ""}
                    </div>
                  </Td>
                  <Td><CategoryBadge category={String(r.category)} /></Td>
                  <Td><Muted>{String(r.size_major || "-")}</Muted></Td>
                  <Td align="right">{fmtNum(r.units_sold)}</Td>
                  <Td align="right" bold>{fmtDollars(r.revenue)}</Td>
                  <Td align="right">
                    <>{fmtDollars(r.cost)}{costUnverified && <span className="text-xs ml-1" style={{ color: "var(--rbw-danger)" }} title="Cost not verified">*</span>}</>
                  </Td>
                  <Td align="right">
                    <span style={{ color: marginColor(mPct), fontWeight: 600 }}>
                      {Number(r.cost) === 0 ? "N/A" : fmtPct(r.margin_pct)}
                    </span>
                  </Td>
                  <Td align="right">{String(r.days_sold)}</Td>
                </TRow>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Card>
  );
}

// ============================================================
// VIEW 3: CATEGORIES
// ============================================================

function CategoryView({ data }: { data: Record<string, unknown> }) {
  const categories = (data.categories || []) as Array<Record<string, unknown>>;
  const total = (data.total || {}) as Record<string, unknown>;
  const totalRevenue = Number(total.revenue) || 1;

  return (
    <div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <UIStatCard label="Total Revenue" value={fmtDollars(total.revenue)} />
        <UIStatCard label="Total Cost" value={fmtDollars(total.cost)} />
        <UIStatCard label="Total Margin" value={fmtDollars(total.margin)} variant="green" />
        <UIStatCard label="Unique Products" value={String(total.unique_products || 0)} variant="blue" />
      </div>

      <Card>
        <CardHeader title="Sales by Category" />
        <Table>
          <Thead>
            <Th>Category</Th>
            <Th align="right">Products</Th>
            <Th align="right">Units</Th>
            <Th align="right">Revenue</Th>
            <Th align="right">% of Total</Th>
            <Th align="right">Cost</Th>
            <Th align="right">Margin</Th>
            <Th align="right">Margin %</Th>
          </Thead>
          <tbody>
            {categories.map((r, i) => {
              const pctOfTotal = (Number(r.revenue) / totalRevenue * 100).toFixed(1);
              const barWidth = Number(r.revenue) / totalRevenue * 100;
              return (
                <TRow key={i} index={i}>
                  <Td><CategoryBadge category={String(r.category)} /></Td>
                  <Td align="right">{String(r.unique_products)}</Td>
                  <Td align="right">{fmtNum(r.units_sold)}</Td>
                  <Td align="right" bold>{fmtDollars(r.revenue)}</Td>
                  <Td align="right">
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-20 h-3 rounded-full overflow-hidden" style={{ background: "var(--rbw-gray-light)" }}>
                        <div className="h-full rounded-full" style={{ width: barWidth + "%", background: "var(--rbw-blue)" }} />
                      </div>
                      <span>{pctOfTotal}%</span>
                    </div>
                  </Td>
                  <Td align="right">{fmtDollars(r.cost)}</Td>
                  <Td align="right">{fmtDollars(r.margin)}</Td>
                  <Td align="right">
                    <span style={{ color: marginColor(Number(r.margin_pct)), fontWeight: 600 }}>
                      {fmtPct(r.margin_pct)}
                    </span>
                  </Td>
                </TRow>
              );
            })}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================================
// VIEW 4: TENDER / PAYMENT TYPE
// ============================================================

function TenderView({ data }: { data: Record<string, unknown> }) {
  const byTender = (data.byTender || []) as Array<Record<string, unknown>>;
  const cashCard = (data.cashCardRatio || []) as Array<Record<string, unknown>>;

  return (
    <div>
      {/* Tender breakdown */}
      <Card className="mb-6">
        <CardHeader title="Revenue by Payment Type" />
        <Table>
          <Thead>
            <Th>Payment Type</Th>
            <Th align="right">Code</Th>
            <Th align="right">Transactions</Th>
            <Th align="right">Line Items</Th>
            <Th align="right">Revenue</Th>
            <Th align="right">% of Total</Th>
          </Thead>
          <tbody>
            {byTender.map((r, i) => {
              const pct = Number(r.pct_of_total) || 0;
              return (
                <TRow key={i} index={i}>
                  <Td bold>{String(r.tender_label)}</Td>
                  <Td align="right"><Muted>{r.tender_code === null ? "-" : String(r.tender_code)}</Muted></Td>
                  <Td align="right">{fmtNum(r.transactions)}</Td>
                  <Td align="right">{fmtNum(r.line_items)}</Td>
                  <Td align="right" bold>{fmtDollars(r.revenue)}</Td>
                  <Td align="right">
                    <div className="flex items-center gap-2 justify-end">
                      <div className="w-24 h-3 rounded-full overflow-hidden" style={{ background: "var(--rbw-gray-light)" }}>
                        <div className="h-full rounded-full" style={{
                          width: pct + "%",
                          background: String(r.tender_label) === "Cash" ? "var(--rbw-gold)" : String(r.tender_label) === "Card" ? "var(--rbw-blue)" : "var(--rbw-gray-dark)",
                        }} />
                      </div>
                      <span>{fmtPct(r.pct_of_total)}</span>
                    </div>
                  </Td>
                </TRow>
              );
            })}
          </tbody>
        </Table>
      </Card>

      {/* Cash vs Card over time */}
      <Card>
        <CardHeader title="Cash vs Card Trend (Monthly)" />
        <Table>
          <Thead>
            <Th>Month</Th>
            <Th align="right">Cash Revenue</Th>
            <Th align="right">Cash %</Th>
            <Th align="right">Card Revenue</Th>
            <Th align="right">Card %</Th>
            <Th align="right">Total</Th>
          </Thead>
          <tbody>
            {cashCard.map((r, i) => (
              <TRow key={i} index={i}>
                <Td bold>{String(r.month_label)}</Td>
                <Td align="right">{fmtDollars(r.cash_revenue)}</Td>
                <Td align="right">
                  <span style={{ color: "var(--rbw-gold-dark)", fontWeight: 600 }}>{fmtPct(r.cash_pct)}</span>
                </Td>
                <Td align="right">{fmtDollars(r.card_revenue)}</Td>
                <Td align="right">
                  <span style={{ color: "var(--rbw-blue-dark)", fontWeight: 600 }}>{fmtPct(r.card_pct)}</span>
                </Td>
                <Td align="right" bold>{fmtDollars(r.total_revenue)}</Td>
              </TRow>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}

// ============================================================
// VIEW 5: CUSTOMERS
// ============================================================

function CustomerView({ data }: { data: Record<string, unknown> }) {
  const topCustomers = (data.topCustomers || []) as Array<Record<string, unknown>>;
  const houseCharge = (data.houseCharge || []) as Array<Record<string, unknown>>;
  const repeatVsOneOff = (data.repeatVsOneOff || {}) as Record<string, Record<string, unknown>>;
  const summary = (data.summary || {}) as Record<string, unknown>;

  return (
    <div>
      {/* Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <UIStatCard label="Named Customers" value={String(summary.named_customers || 0)} />
        <UIStatCard label="Named Revenue" value={fmtDollars(summary.named_revenue)} variant="gold" />
        <UIStatCard
          label="Repeat Customers"
          value={String(repeatVsOneOff.repeat?.customer_count || 0)}
          detail={`Revenue: ${fmtDollars(repeatVsOneOff.repeat?.total_revenue)}`}
          variant="green"
        />
        <UIStatCard
          label="One-Off Customers"
          value={String(repeatVsOneOff.one_off?.customer_count || 0)}
          detail={`Revenue: ${fmtDollars(repeatVsOneOff.one_off?.total_revenue)}`}
          variant="blue"
        />
      </div>

      {/* House charge accounts */}
      {houseCharge.length > 0 && (
        <Card className="mb-6">
          <CardHeader title="House Charge / On-Account (Tender 16)" />
          <Table>
            <Thead>
              <Th>Customer</Th>
              <Th align="right">Charge Days</Th>
              <Th align="right">Transactions</Th>
              <Th align="right">Total Charged</Th>
              <Th>First Charge</Th>
              <Th>Last Charge</Th>
            </Thead>
            <tbody>
              {houseCharge.map((r, i) => (
                <TRow key={i} index={i}>
                  <Td bold>{String(r.customer_name)}</Td>
                  <Td align="right">{String(r.charge_days)}</Td>
                  <Td align="right">{String(r.transactions)}</Td>
                  <Td align="right" bold>{fmtDollars(r.total_charged)}</Td>
                  <Td>{fmtDate(r.first_charge)}</Td>
                  <Td>{fmtDate(r.last_charge)}</Td>
                </TRow>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Top customers */}
      <Card>
        <CardHeader title="Top 50 Customers by Revenue" />
        <div style={{ maxHeight: 600, overflowY: "auto" }}>
          <Table>
            <Thead>
              <Th>Customer</Th>
              <Th align="right">Visit Days</Th>
              <Th align="right">Transactions</Th>
              <Th align="right">Revenue</Th>
              <Th align="right">Avg/Visit</Th>
              <Th>First Seen</Th>
              <Th>Last Seen</Th>
            </Thead>
            <tbody>
              {topCustomers.map((r, i) => (
                <TRow key={i} index={i}>
                  <Td bold>{String(r.customer_name)}</Td>
                  <Td align="right">{String(r.visit_days)}</Td>
                  <Td align="right">{String(r.transactions)}</Td>
                  <Td align="right" bold>{fmtDollars(r.revenue)}</Td>
                  <Td align="right">{fmtDollars(r.avg_per_visit)}</Td>
                  <Td>{fmtDate(r.first_seen)}</Td>
                  <Td>{fmtDate(r.last_seen)}</Td>
                </TRow>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
